import { ReactElement, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Link, Typography } from '@mui/material';
import { LogoIcon } from '@localstack/ui';

import { BaseLayout } from '~/layouts';
import { ConsoleRedirectProps } from '~/views/customer/Redirect/Redirect';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import {
  MainContainer,
  ContentContainer,
  LogoContainer,
  InstructionsContainer,
  SubText,
  FooterContainer,
  EmailText,
} from '~/views/customer/Redirect/components';

const EXTENSION_NAME = 'localstack.localstack';

enum KnownEditors {
  VSCode = 'vscode',
  VSCodeInsiders = 'vscode-insiders',
  Windsurf = 'windsurf',
  Kiro = 'kiro',
  Cursor = 'cursor',
  Antigravity = 'antigravity',
}

/**
 * Supported editors for LocalStack Toolkit authentication.
 * Maps name query param to its deep link protocol.
 */
const NAME_TO_PROTOCOL: Record<KnownEditors, string> = {
  [KnownEditors.VSCode]: 'vscode',
  [KnownEditors.VSCodeInsiders]: 'vscode-insiders',
  [KnownEditors.Windsurf]: 'windsurf',
  [KnownEditors.Kiro]: 'kiro',
  [KnownEditors.Cursor]: 'cursor',
  [KnownEditors.Antigravity]: 'antigravity',
};

const DEFAULT_PROTOCOL = 'vscode';

const NAME_QUERY_PARAM = 'name';
const WINDOW_ID_QUERY_PARAM = 'windowId';
const TOKEN_QUERY_PARAM = 'token';

const getRedirectUrl = (name: string | null, token?: string, windowId?: string | null) => {
  const protocol = NAME_TO_PROTOCOL[name?.toLowerCase() as KnownEditors] ?? DEFAULT_PROTOCOL;
  const url = new URL(`${protocol}://${EXTENSION_NAME}/auth`);
  if (token) url.searchParams.set(TOKEN_QUERY_PARAM, token);
  if (windowId) url.searchParams.set(WINDOW_ID_QUERY_PARAM, windowId);
  return url.toString();
};

/**
 * Handles authentication redirects for LocalStack Toolkit extensions.
 * Fetches the auth token and redirects back to the requesting editor
 * (VS Code, VS Code Insiders, and other supported editors).
 */
export const ToolkitExtensionRedirect = ({ token }: ConsoleRedirectProps): ReactElement => {
  const [searchParams] = useSearchParams();
  const windowIdValue = searchParams.get(WINDOW_ID_QUERY_PARAM);
  const name = searchParams.get(NAME_QUERY_PARAM);
  const { userInfo } = useAuthProvider();

  const redirectUrl = getRedirectUrl(name, token, windowIdValue);

  const redirectToEditor = useCallback(() => {
    window.location.href = redirectUrl;
  }, [redirectUrl]);

  useEffect(() => {
    if (token) {
      redirectToEditor();
    }
  }, [token, redirectToEditor]);

  return (
    <BaseLayout title="LocalStack Toolkit" hideNavigation>
      <MainContainer>
        <ContentContainer>
          <LogoContainer>
            <LogoIcon />
          </LogoContainer>
          <InstructionsContainer>
            <Typography textAlign="center" variant="h2" component="h1" fontWeight={600}>
              LocalStack is ready to use
            </Typography>
            <SubText>You can close this window.</SubText>
            <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mt: 2 }}>
              Redirect not working?{' '}
              <Link href={redirectUrl} underline="always" color="inherit" sx={{ textUnderlineOffset: '3px' }}>
                Try again
              </Link>
            </Typography>
          </InstructionsContainer>
        </ContentContainer>
        <FooterContainer>
          <EmailText>Logged in as {userInfo?.user?.email}</EmailText>
        </FooterContainer>
      </MainContainer>
    </BaseLayout>
  );
};
